import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { HANDBOOK_TEMPLATE_LVL } from "pages/HandBook/model/slice";
import {
  getHandbookTemplateExpenditures,
  getHandbookTemplateExpendituresMore,
  getHandbookTemplateSections,
  getHandbookTemplates,
} from "pages/HandBook/model/thunks";
import { IHandBookTemplateExpenditure } from "pages/HandBook/model/types";
import { handbooksModifyWithEditings } from "pages/HandBook/utils/handbooksModifyWithEditings";
import CreateHandbookTemplateBtn from "pages/HandBook/widgets/CreateHandbook/CreateHandbookTemplateBtn";
import CreateHandbookTemplateExpenditure from "pages/HandBook/widgets/CreateHandbook/CreateHandbookTemplateExpenditure/CreateHandbookTemplateExpenditure";

import icon from "./../../../../assets/handbook.svg";
import HandBooksTemplateExpenditureRow from "./HandBooksTemplateExpenditureRow";
import { useHandbookTemplatesExpendituresColumns } from "./useHandbookTemplatesExpendituresColumns";
import { useTypedSelector } from "app/store/typedUseSelector";
import TableReusableHead, { TableReusableHeaderCell } from "shared/ui/dataDisplay/TableReusable/TableReusableHead";
import BackNavigationBar from "shared/ui/layout/BackNavigationBar/BackNavigationBar";
import ListWrapper from "shared/ui/layout/ListWrapper/ListWrapper";
import RowGrid from "shared/ui/layout/RowGrid/RowGrid";

import { generateStorageKey } from "utils/helpers/generateStorageKey";

const HandBooksTemplateExpenditures = () => {
  const history = useHistory();
  const { templateId, sectionId, subSectionId } = useParams<{
    templateId: string;
    sectionId?: string;
    subSectionId?: string;
  }>();
  const [filter, setFilter] = useState<any>(() => ({
    template_id: templateId,
    section_id: subSectionId,
  }));
  const hasListFilters = Object.keys(filter).length > 2;

  const { limit, offset, ...restFilter } = filter;
  const key = generateStorageKey(restFilter as any);
  const moreKey = generateStorageKey({
    ...restFilter,
    type: "moreTemplateSections",
  } as any);
  const dispatch = useDispatch();
  const cacheKey = useTypedSelector((state) => state.handBookReducer.invalidationKey);
  // @ts-ignore | Ключ всех шаблонов – пустой фильтр. Не будет находить шаблон, если был переход по прямой ссылке
  const templates = useTypedSelector((state) => state.handBookReducer.templates)[generateStorageKey({})];
  const sections = useTypedSelector((state) => state.handBookReducer.templateSections)[
    // @ts-ignore | Секции выбранного шаблона
    generateStorageKey({ template_id: templateId })
  ];
  const expenditures = useTypedSelector((state) => state.handBookReducer.templateExpenditures)[key];
  const editings = useTypedSelector((state) => state.handBookReducer.templateEditings)[
    HANDBOOK_TEMPLATE_LVL.EXPENDITURES
  ];
  const isEditing = useTypedSelector((state) => state.handBookReducer.templateEditings.isEditing);

  const { currentTemplate, currentSection, currentSubSection } = useMemo(() => {
    const currentTemplate = templateId ? templates?.results?.find((t) => +t.id === +templateId) : null;
    const currentSection = sectionId ? sections?.results?.find((s) => +s.id === +sectionId) : null;
    const currentSubSection = subSectionId ? currentSection?.children?.find((s) => +s.id === +subSectionId) : null;
    return { currentTemplate, currentSection, currentSubSection };
  }, [subSectionId, sectionId, templateId, sections, templates]);

  const isLoading = useTypedSelector((state) => state.handBookReducer.loadings)[key];
  const isLoadingMore = useTypedSelector((state) => state.handBookReducer.loadings)[moreKey];

  const displayExpenditures = useMemo(() => {
    if (!expenditures?.results) return expenditures?.results;
    return handbooksModifyWithEditings(expenditures.results, editings);
  }, [expenditures?.results, editings]);

  const { columns, colPercents, formColPercents } = useHandbookTemplatesExpendituresColumns({ isEditing });

  useEffect(() => {
    if (!templates?.results?.length) {
      dispatch(getHandbookTemplates({ limit: 500 }));
    }
    if (!sections?.results?.length) {
      dispatch(
        getHandbookTemplateSections({
          template_id: templateId,
          limit: 500,
        })
      );
    }
    dispatch(
      getHandbookTemplateExpenditures({
        ...filter,
        limit: Math.max(expenditures?.results?.length ?? 10, 10) + 1,
      })
    );
  }, [cacheKey, filter]);

  const onPagination = () => {
    dispatch(getHandbookTemplateExpendituresMore({ ...filter, offset: expenditures?.results?.length }));
  };

  const onBack = () => {
    // Убираем крайний численный параметр
    history.replace(history.location.pathname.replace(/\/\d+\/?$/, ""));
  };

  return (
    <ListWrapper
      data={displayExpenditures}
      dataMapper={(item: IHandBookTemplateExpenditure) => (
        <HandBooksTemplateExpenditureRow
          item={item}
          sectionId={+subSectionId!}
          key={item.id}
          colPercents={colPercents}
          formColPercents={formColPercents}
          columns={columns}
        />
      )}
      header={
        <React.Fragment>
          <BackNavigationBar
            isInNestedList
            onBack={onBack}
            title={currentTemplate?.name || "Шаблоны"}
            slashTitle={(currentSection?.name || "") + " / " + (currentSubSection?.name || "")}
            isBold
          />
          <TableReusableHead isSticky isNoDefaultGridCols>
            <RowGrid percents={colPercents}>
              {columns.map((el) => (
                <TableReusableHeaderCell
                  key={el.header.title}
                  isCentered={el.isCentered}
                  filterType={el.header.filterType ? el.header.filterType : el.header.filterName ? "text" : undefined}
                  filterField={el.header.filterName}
                  //@ts-ignore
                  onChangeFilter={(name, value) => setFilter((prev) => ({ ...prev, [name]: value }))}
                  filterTitle={el.header.title}
                  isLast={el.isLast}
                  filterOptions={el.header.filterOptions}
                  isRighted={el.isRighted}
                  isDebouncedFilterApply
                >
                  {el.header.title}
                </TableReusableHeaderCell>
              ))}
            </RowGrid>
          </TableReusableHead>
        </React.Fragment>
      }
      placeholderProps={{ img: icon, text: hasListFilters ? "Нет сведений" : "Добавьте работы", fullHeight: false }}
      total={expenditures?.count}
      isLoading={isLoading}
      isLoadingMore={isLoadingMore}
      filter={filter}
      onPagination={onPagination}
      postfix={
        !isLoading && (
          <>
            <CreateHandbookTemplateExpenditure sectionId={subSectionId!} />{" "}
            <CreateHandbookTemplateBtn text="Добавить работу" />
          </>
        )
      }
    />
  );
};

export default HandBooksTemplateExpenditures;
